import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  asyncDataLoaderFeature,
  hotkeysCoreFeature,
  searchFeature,
  selectionFeature,
  type ItemInstance,
  type TreeInstance,
} from '@headless-tree/core';
import { useTree } from '@headless-tree/react';
import { useVirtualizer, type Virtualizer } from '@tanstack/react-virtual';
import { Spinner } from './spinner';
import { cn } from '../lib/utils';

export interface FileTreeEntry {
  path: string;
  name: string;
  isDir: boolean;
  size: number;
  /**
   * Content version token (sha256 of content, from cloud sandbox / local daemon
   * list ops) — the change-detection currency for tree refresh and open-tab
   * external-change diffs. Absent/null for
   * directories and for sources that don't compute one.
   */
  version?: string | null;
}

/** Imperative surface exposed through {@link FileTreeProps.refreshRef}. */
export interface FileTreeRefreshHandle {
  /**
   * Re-fetch the root and every currently-expanded directory through the
   * async loader cache, then prune expansion entries whose paths vanished.
   * Resolves when all reloads have landed; per-directory load failures are
   * NOT rejections — they surface in the inline error badges (fail-visible).
   */
  refresh(): Promise<void>;
}

export interface FileTreeProps {
  /** The already-loaded root-level entries (the parent controls initial loading). */
  entries: FileTreeEntry[];
  /** Called on directory expand to lazily fetch that directory's direct children. */
  loadChildren: (path: string) => Promise<FileTreeEntry[]>;
  selectedPath?: string | null;
  onSelect?: (entry: FileTreeEntry) => void;
  onSelectFolder?: (entry: FileTreeEntry) => void;
  defaultExpandedPaths?: string[];
  /**
   * When provided, each node (file OR directory) becomes a native HTML5 drag
   * source; the handler owns the DataTransfer payload (e.g. a mention ref for
   * the composer).
   */
  onNodeDragStart?: (entry: FileTreeEntry, e: React.DragEvent) => void;
  /**
   * When provided, right-clicking a node (file OR directory), or pressing the
   * Menu key / Shift+F10 on a focused row, opens the consumer's context menu:
   * the row prevents the native menu and hands the handler the entry plus
   * viewport coordinates (the pointer, or the row's bottom-left for the
   * keyboard). The handler owns all menu rendering.
   */
  onNodeContextMenu?: (entry: FileTreeEntry, pos: { x: number; y: number }) => void;
  onBackgroundContextMenu?: (pos: { x: number; y: number }) => void;
  /**
   * Imperative refresh handle for the host's refresh controller:
   * reconciles the tree in place via the
   * async-loader cache — no remount, expansion/selection of survivors
   * preserved by construction (ids are paths).
   */
  refreshRef?: React.Ref<FileTreeRefreshHandle>;
}

/** Item ids are workspace-relative paths; the (virtual) root is the empty path. */
const ROOT_ID = '';

const ROOT_ENTRY: FileTreeEntry = { path: ROOT_ID, name: '', isDir: true, size: 0 };

/** Placeholder item data rendered while an item's real data is still loading. */
const LOADING_ENTRY: FileTreeEntry = { path: '', name: '', isDir: false, size: 0 };

const ESTIMATED_ROW_HEIGHT = 29;

/** Directories first, then alphabetical by name. */
function compareEntries(a: FileTreeEntry, b: FileTreeEntry): number {
  if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
  return a.name.localeCompare(b.name);
}

/** Parent directory path of a workspace-relative path ('' = root). */
function parentPathOf(path: string): string {
  const idx = path.lastIndexOf('/');
  return idx === -1 ? ROOT_ID : path.slice(0, idx);
}

function FolderIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0 text-muted-foreground"
    >
      <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0 text-muted-foreground"
    >
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('shrink-0 transition-transform', expanded && 'rotate-90')}
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </svg>
  );
}

/**
 * Read-only, lazily-expanding directory tree built on `@headless-tree` with
 * `@tanstack/react-virtual` row virtualization.
 *
 * The parent supplies the already-loaded root `entries`; each directory's
 * direct children are fetched on first expand via `loadChildren` (lazy, single
 * level, cached — one call per directory across expand/collapse/re-expand).
 * Keyboard traversal (arrows/Home/End/Enter) and letter type-ahead follow the
 * W3C treeview pattern via `hotkeysCoreFeature` + `searchFeature`. No
 * create/rename/delete/move affordances are rendered.
 */
export function FileTree({
  entries,
  loadChildren,
  selectedPath,
  onSelect,
  onSelectFolder,
  defaultExpandedPaths,
  onNodeDragStart,
  onNodeContextMenu,
  onBackgroundContextMenu,
  refreshRef,
}: FileTreeProps) {
  // Expansion is CONTROLLED component state (string[] of paths) so future
  // refresh controllers can read and reconcile it.
  const [expandedItems, setExpandedItems] = useState<string[]>(() => defaultExpandedPaths ?? []);
  // Per-directory load failures keyed by path; rendered as an inline badge on
  // the directory row (fail-visible). Re-expanding an errored dir retries.
  const [errorPaths, setErrorPaths] = useState<Map<string, string>>(new Map());

  // Latest-value refs: the headless-tree data loader and hotkey handlers are
  // invoked from the tree instance outside the render cycle.
  const entriesRef = useRef(entries);
  const loadChildrenRef = useRef(loadChildren);
  loadChildrenRef.current = loadChildren;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const onSelectFolderRef = useRef(onSelectFolder);
  onSelectFolderRef.current = onSelectFolder;
  const keyboardMenuRef = useRef<{ path: string; at: number } | null>(null);
  const expandedItemsRef = useRef(expandedItems);
  expandedItemsRef.current = expandedItems;
  const errorPathsRef = useRef(errorPaths);
  errorPathsRef.current = errorPaths;
  // Every entry ever returned by the loader, for getItem lookups (e.g. after a
  // cache reconcile sets children ids before data is re-fetched).
  const knownEntriesRef = useRef(new Map<string, FileTreeEntry>());
  const treeRef = useRef<TreeInstance<FileTreeEntry> | null>(null);
  const virtualizerRef = useRef<Virtualizer<HTMLDivElement, Element> | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const clearErrorPath = (path: string) => {
    setErrorPaths((prev) => {
      if (!prev.has(path)) return prev;
      const next = new Map(prev);
      next.delete(path);
      return next;
    });
  };

  const tree = useTree<FileTreeEntry>({
    rootItemId: ROOT_ID,
    state: {
      expandedItems,
      selectedItems: typeof selectedPath === 'string' ? [selectedPath] : [],
    },
    // Selection is controlled purely by the `selectedPath` prop: the selection
    // feature's own click-writes are overridden by the controlled state on the
    // next render, and `onSelect` fires from primaryAction on files only.
    setExpandedItems: (updaterOrValue) => {
      const prev = expandedItemsRef.current;
      const next = typeof updaterOrValue === 'function' ? updaterOrValue(prev) : updaterOrValue;
      setExpandedItems(next);
      // Re-expanding a directory whose last load failed retries the fetch.
      for (const id of next) {
        if (prev.includes(id) || !errorPathsRef.current.has(id)) continue;
        const instance = treeRef.current;
        if (instance === null) throw new Error('FileTree: expansion changed before the tree mounted');
        clearErrorPath(id);
        void instance.getItemInstance(id).invalidateChildrenIds();
      }
    },
    isItemFolder: (item: ItemInstance<FileTreeEntry>) => item.getItemData().isDir,
    getItemName: (item: ItemInstance<FileTreeEntry>) => item.getItemData().name,
    onPrimaryAction: (item: ItemInstance<FileTreeEntry>) => {
      const entry = item.getItemData();
      if (entry.isDir) onSelectFolderRef.current?.(entry);
      else onSelectRef.current?.(entry);
    },
    // Keyboard focus moves (End on a 10k list, type-ahead) must scroll the
    // virtualized row into existence before the DOM focus poll runs.
    scrollToItem: (item: ItemInstance<FileTreeEntry>) => {
      virtualizerRef.current?.scrollToIndex(item.getItemMeta().index);
    },
    createLoadingItemData: () => LOADING_ENTRY,
    dataLoader: {
      getItem: (itemId: string): FileTreeEntry => {
        if (itemId === ROOT_ID) return ROOT_ENTRY;
        const known = knownEntriesRef.current.get(itemId);
        if (known === undefined) throw new Error(`FileTree: no loaded entry for item "${itemId}"`);
        return known;
      },
      // One loadChildren call per directory; ids are workspace-relative paths.
      // Dirs-first + alphabetical ordering is applied here.
      getChildrenWithData: async (dirPath: string) => {
        try {
          const list = dirPath === ROOT_ID ? entriesRef.current : await loadChildrenRef.current(dirPath);
          const sorted = [...list].sort(compareEntries);
          for (const entry of sorted) knownEntriesRef.current.set(entry.path, entry);
          clearErrorPath(dirPath);
          return sorted.map((entry) => ({ id: entry.path, data: entry }));
        } catch (err) {
          // Fail-visible, never silent: the failure renders as an inline badge
          // on the directory row, and the dir stays collapsible; re-expanding
          // retries via invalidateChildrenIds (see setExpandedItems above).
          setErrorPaths((prev) => {
            const next = new Map(prev);
            next.set(dirPath, err instanceof Error ? err.message : String(err));
            return next;
          });
          return [];
        }
      },
    },
    features: [asyncDataLoaderFeature, selectionFeature, hotkeysCoreFeature, searchFeature],
    hotkeys: {
      // headless-tree ships no Enter activation of its own (Enter is bound
      // only by search/dnd); wire the W3C treeview behavior: Enter toggles a
      // focused directory and activates (selects) a focused file.
      customPrimaryAction: {
        hotkey: 'Enter',
        preventDefault: true,
        isEnabled: (t: TreeInstance<FileTreeEntry>) => !t.isSearchOpen(),
        handler: (_e: KeyboardEvent, t: TreeInstance<FileTreeEntry>) => {
          const item = t.getFocusedItem();
          if (item.isFolder()) {
            if (item.isExpanded()) {
              item.collapse();
            } else {
              item.expand();
            }
          } else {
            item.primaryAction();
          }
        },
      },
    },
  });
  treeRef.current = tree;

  // Drop expansion entries whose path no longer exists in its parent's
  // (freshly cached) children. Parents of visible expanded dirs are always
  // cached (they were loaded to render), so `loadChildrenIds` resolves from
  // cache; a collapsed-but-remembered ancestor may still hold a stale cache —
  // bounded staleness accepted, corrected on its next reload.
  const pruneVanishedExpansions = async (instance: TreeInstance<FileTreeEntry>): Promise<void> => {
    const expanded = expandedItemsRef.current;
    if (expanded.length === 0) return;
    const childIdsByParent = new Map<string, string[]>();
    await Promise.all(
      [...new Set(expanded.map(parentPathOf))].map(async (parent) => {
        childIdsByParent.set(parent, await instance.loadChildrenIds(parent));
      }),
    );
    // Functional update: a dir the user expanded WHILE the refresh was in
    // flight has no fetched parent listing here — keep it (never clobber a
    // concurrent user expand); only drop ids proven absent from fresh data.
    setExpandedItems((prev) => {
      const survivors = prev.filter((id) => {
        const siblings = childIdsByParent.get(parentPathOf(id));
        return siblings === undefined || siblings.includes(id);
      });
      return survivors.length === prev.length ? prev : survivors;
    });
  };

  // Imperative refresh: re-fetch root, then expanded dirs top-down, reloading a
  // dir only while its fresh parent listing still contains it — a removed
  // folder is never listed — and drop the expansions of the vanished ones.
  // `optimistic: true` — the current children stay visible while the reload is
  // in flight (no loading flash / no row collapse); vanished detection is
  // still exact because pruning reads the FRESH ids after the awaited reload.
  // Per-dir load failures never reject: getChildrenWithData catches them into
  // the errorPaths badge map (fail-visible) and yields an empty child list.
  useImperativeHandle(
    refreshRef,
    () => ({
      refresh: async () => {
        const instance = treeRef.current;
        if (instance === null) throw new Error('FileTree: refresh called before the tree mounted');
        const snapshot = expandedItemsRef.current;
        const byDepth = new Map<number, string[]>();
        for (const dir of snapshot) {
          const depth = dir.split('/').length;
          byDepth.set(depth, [...(byDepth.get(depth) ?? []), dir]);
        }
        await instance.getItemInstance(ROOT_ID).invalidateChildrenIds(true);
        const live = new Set<string>([ROOT_ID]);
        for (const depth of [...byDepth.keys()].sort((x, y) => x - y)) {
          await Promise.all(
            (byDepth.get(depth) ?? []).map(async (dir) => {
              const parent = parentPathOf(dir);
              if (!live.has(parent)) return;
              if (!(await instance.loadChildrenIds(parent)).includes(dir)) return;
              await instance.getItemInstance(dir).invalidateChildrenIds(true);
              live.add(dir);
            }),
          );
        }
        setExpandedItems((prev) => {
          const survivors = prev.filter((id) => !snapshot.includes(id) || live.has(id));
          return survivors.length === prev.length ? prev : survivors;
        });
      },
    }),
    // Empty deps: the handle reads only live refs (treeRef/expandedItemsRef)
    // and the stable setExpandedItems, so it never needs to re-bind.
    [],
  );

  // Root entries arrive as a prop; when the parent hands us a new array,
  // reconcile through the async loader's cache (optimistic: current rows stay
  // until the new roots land — no loading flash). Prune expansions afterwards
  // so a top-level dir that vanished from the fresh root drops its stale
  // expansion entry too.
  useEffect(() => {
    if (entriesRef.current === entries) return;
    entriesRef.current = entries;
    void tree
      .getItemInstance(ROOT_ID)
      .invalidateChildrenIds(true)
      .then(() => pruneVanishedExpansions(tree));
    // pruneVanishedExpansions reads live refs only — safe to omit from deps.
  }, [entries, tree]);

  const items = tree.getItems();

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    getItemKey: (index) => items[index]?.getId() ?? `row-${index}`,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 10,
  });
  virtualizerRef.current = virtualizer;

  const { ref: registerContainer, ...containerProps } = tree.getContainerProps('Files') as {
    ref: (el: HTMLElement | null) => void;
  } & React.HTMLAttributes<HTMLDivElement>;

  return (
    <div className="relative h-full">
      <div
        {...containerProps}
        ref={(el) => {
          registerContainer(el);
          scrollRef.current = el;
        }}
        className="h-full select-none overflow-auto focus:outline-none"
        onContextMenu={
          onBackgroundContextMenu
            ? (e) => {
                containerProps.onContextMenu?.(e);
                if (e.target instanceof HTMLInputElement) return;
                e.preventDefault();
                onBackgroundContextMenu({ x: e.clientX, y: e.clientY });
              }
            : containerProps.onContextMenu
        }
      >
        {/* Inside the container: the hotkeys feature's keydown listener is on
            the registered container element, so Escape/Enter in the input must
            bubble THROUGH it to close the search. Sticky so it stays pinned
            while the virtualized list scrolls. */}
        {tree.isSearchOpen() && (
          <input
            {...(tree.getSearchInputElementProps() as React.InputHTMLAttributes<HTMLInputElement> & {
              ref: (el: HTMLInputElement | null) => void;
            })}
            aria-label="Type-ahead search"
            data-testid="file-tree-search"
            className="sticky left-1 top-1 z-10 w-40 rounded-sm bg-muted px-1 text-body focus:outline-none focus:ring-1 focus:ring-ring"
          />
        )}
        <div style={{ height: `${virtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}>
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const item = items[virtualRow.index];
            const entry = item.getItemData();
            const isDir = item.isFolder();
            const isExpanded = item.isExpanded();
            const isLoading = item.isLoading();
            const errorMessage = errorPaths.get(item.getId());
            const { ref: registerItem, ...itemProps } = item.getProps() as {
              ref: (el: HTMLElement | null) => void;
            } & React.HTMLAttributes<HTMLDivElement>;

            return (
              <div
                key={item.getId()}
                data-index={virtualRow.index}
                {...itemProps}
                ref={(el) => {
                  virtualizer.measureElement(el);
                  registerItem(el);
                }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                  paddingLeft: `${item.getItemMeta().level * 20 + 4}px`,
                }}
                className="focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                // Drag source on the ROW (the role=treeitem element): the whole
                // row drags, and consumers/tests target the accessible element.
                draggable={onNodeDragStart ? true : undefined}
                onDragStart={onNodeDragStart ? (e) => onNodeDragStart(entry, e) : undefined}
                onContextMenu={
                  onNodeContextMenu
                    ? (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const kb = keyboardMenuRef.current;
                        keyboardMenuRef.current = null;
                        if (kb !== null && kb.path === entry.path && e.timeStamp - kb.at < 1000) return;
                        onNodeContextMenu(entry, { x: e.clientX, y: e.clientY });
                      }
                    : undefined
                }
                onKeyDown={
                  onNodeContextMenu
                    ? (e) => {
                        itemProps.onKeyDown?.(e);
                        if (e.key !== 'ContextMenu' && !(e.key === 'F10' && e.shiftKey)) return;
                        e.preventDefault();
                        e.stopPropagation();
                        keyboardMenuRef.current = { path: entry.path, at: e.timeStamp };
                        const rect = e.currentTarget.getBoundingClientRect();
                        onNodeContextMenu(entry, { x: rect.left, y: rect.bottom });
                      }
                    : itemProps.onKeyDown
                }
              >
                <div
                  className={cn(
                    'group flex cursor-pointer items-center gap-1 rounded-sm px-1 py-0.5',
                    item.isSelected() ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50',
                  )}
                >
                  {isDir ? (
                    <button
                      type="button"
                      className="shrink-0 rounded-sm p-0.5 hover:bg-accent"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isExpanded) {
                          item.collapse();
                        } else {
                          item.expand();
                        }
                      }}
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      <ChevronIcon expanded={isExpanded} />
                    </button>
                  ) : (
                    <span className="w-[22px] shrink-0" />
                  )}

                  {isDir ? <FolderIcon /> : <FileIcon />}

                  <span className="min-w-0 truncate text-body">{entry.name}</span>

                  {isLoading && <Spinner size="sm" className="ml-1 shrink-0 text-muted-foreground" />}

                  {errorMessage !== undefined && (
                    <span className="ml-1 text-destructive" title={errorMessage} role="alert" aria-label={errorMessage}>
                      <ErrorIcon />
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
